import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { borrowerAPI, paymentsAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  DollarSign, FileText, CreditCard, CheckCircle, Clock, 
  AlertTriangle, Shield, Bell, PartyPopper
} from 'lucide-react';
import DocumentUpload from '../../components/DocumentUpload';
import DocumentList from '../../components/DocumentList';

const BorrowerDealDetail = () => {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [fees, setFees] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    fetchDeal();
    fetchFees();
    fetchPaymentStatus();
    fetchNotifications();
  }, [dealId]);

  const fetchDeal = async () => {
    try {
      const response = await borrowerAPI.getDeal(dealId);
      setDeal(response.data);
    } catch (error) {
      toast.error('Failed to load deal');
    } finally {
      setLoading(false);
    }
  };

  const fetchFees = async () => {
    try {
      const response = await borrowerAPI.getDealFees(dealId);
      setFees(response.data);
    } catch (error) {
      console.error('Failed to load fees');
    }
  };

  const fetchPaymentStatus = async () => {
    try {
      const response = await paymentsAPI.getAuthorizationStatus(dealId);
      setPaymentStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch payment status');
    }
  };

  const fetchNotifications = async () => {
    try {
      // Fetch deal-related notifications (we'll use the API if available)
      const response = await borrowerAPI.getDeals();
      // Filter for partnership notifications
      // For now, we'll check if deal is in a partnered state
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  };

  const handleAuthorizePayment = async () => {
    setPaymentLoading(true);
    try {
      const response = await paymentsAPI.setupAuthorization(dealId);
      toast.info(response.data.message);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup payment authorization');
      setPaymentLoading(false);
    }
  };

  if (loading) return <Layout><div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div></Layout>;
  if (!deal) return <Layout><p className="text-center py-12 text-slate-600">Deal not found</p></Layout>;

  // Check if partnered (commitment approved)
  const isPartnered = deal.status === 'reserved' || deal.status === 'closing' || paymentStatus?.paid;

  return (
    <Layout title={deal.deal_nickname}>
      <div className="space-y-6 animate-slide-in-up" data-testid="deal-detail">
        
        {/* Partnership Notification Banner */}
        {isPartnered && (
          <Card className="glass-effect border-green-300 dark:border-green-700 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 animate-scale-in">
            <CardContent className="py-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                  <PartyPopper className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-green-800 dark:text-green-200 mb-1">
                    Congratulations! You've Been Partnered!
                  </h3>
                  <p className="text-green-700 dark:text-green-300">
                    An LSP has committed to your deal. The $750 intake fee has been charged to your authorized payment method.
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                    <strong>Important:</strong> Please ensure sufficient funds are available in your connected account for closing costs.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Deal Information */}
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
                <div className="w-10 h-10 gradient-primary rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                Deal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between py-2 border-b border-slate-200 dark:border-slate-700">
                <span className="text-slate-600 dark:text-slate-400">Status:</span>
                <span className={`px-3 py-1 rounded font-medium text-sm ${
                  deal.status === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                  deal.status === 'reserved' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400' :
                  'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                }`}>{deal.status.toUpperCase()}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-200 dark:border-slate-700">
                <span className="text-slate-600 dark:text-slate-400">Property:</span>
                <span className="font-medium text-slate-900 dark:text-slate-100">{deal.property_city}, {deal.property_state}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-200 dark:border-slate-700">
                <span className="text-slate-600 dark:text-slate-400">Purchase Price:</span>
                <span className="font-medium text-slate-900 dark:text-slate-100">${deal.purchase_price?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-200 dark:border-slate-700">
                <span className="text-slate-600 dark:text-slate-400">Rehab Budget:</span>
                <span className="font-medium text-slate-900 dark:text-slate-100">${deal.rehab_budget?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-200 dark:border-slate-700">
                <span className="text-slate-600 dark:text-slate-400">ARV:</span>
                <span className="font-medium text-slate-900 dark:text-slate-100">${deal.arv?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-200 dark:border-slate-700">
                <span className="text-slate-600 dark:text-slate-400">Reserve Required:</span>
                <span className="font-semibold text-green-600 dark:text-green-400">${deal.reserve_required_amount?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-slate-600 dark:text-slate-400">Liquidity Provided:</span>
                <span className="font-semibold text-blue-600 dark:text-blue-400">${deal.liquidity_amount_provided?.toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>

          {/* Fee Breakdown */}
          {fees && (
            <Card className="glass-effect border-slate-200 dark:border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
                  <div className="w-10 h-10 gradient-success rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                  Fee Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between py-2 border-b border-slate-200 dark:border-slate-700">
                  <span className="text-slate-600 dark:text-slate-400">Upfront Intake Fee:</span>
                  <span className="font-medium text-slate-900 dark:text-slate-100">${fees.upfront_intake_fee?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-200 dark:border-slate-700">
                  <span className="text-slate-600 dark:text-slate-400">Liquidity Coordination Fee (5%):</span>
                  <span className="font-medium text-slate-900 dark:text-slate-100">${fees.liquidity_coordination_fee?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-200 dark:border-slate-700">
                  <span className="text-slate-600 dark:text-slate-400">Processing Fee:</span>
                  <span className="font-medium text-slate-900 dark:text-slate-100">${fees.processing_fee?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-200 dark:border-slate-700">
                  <span className="text-slate-600 dark:text-slate-400">Wire Fee:</span>
                  <span className="font-medium text-slate-900 dark:text-slate-100">${fees.wire_fee?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-3 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg px-4 mt-2">
                  <span className="font-semibold text-slate-900 dark:text-slate-100">Total Closing Fees:</span>
                  <span className="text-lg font-bold text-green-600 dark:text-green-400">${fees.total_closing_fees?.toFixed(2)}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Payment Authorization Section */}
        {deal.status === 'submitted' && (
          <Card className="glass-effect border-blue-200 dark:border-blue-800 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 animate-scale-in">
            <CardContent className="py-6">
              {paymentStatus?.authorized ? (
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">Payment Authorized</h3>
                  <p className="text-slate-600 dark:text-slate-400 mb-4">
                    Your $750 intake fee is authorized and will be charged when an LSP commits to your deal.
                  </p>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 dark:bg-green-900/30 rounded-full text-green-700 dark:text-green-400">
                    <CreditCard className="w-4 h-4" />
                    Payment method saved securely
                  </div>
                </div>
              ) : paymentStatus?.paid ? (
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">Payment Complete</h3>
                  <p className="text-slate-600 dark:text-slate-400">
                    Your $750 intake fee has been paid. An LSP has committed to your deal!
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                    <CreditCard className="w-8 h-8 text-blue-500" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">Authorize Payment</h3>
                  <p className="text-slate-600 dark:text-slate-400 mb-2">
                    Authorize the $750 intake fee to proceed. <strong>You won't be charged until an LSP commits to your deal.</strong>
                  </p>
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 mb-4 text-left border border-amber-200 dark:border-amber-800">
                    <div className="flex items-start gap-2">
                      <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-700 dark:text-amber-400">
                        Your card will be securely saved. The fee is only charged when you're matched with an LSP partner.
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handleAuthorizePayment}
                    disabled={paymentLoading}
                    className="w-full h-12 gradient-primary border-0 hover:shadow-xl hover-scale"
                    data-testid="authorize-payment-button"
                  >
                    <CreditCard className="w-5 h-5 mr-2" />
                    {paymentLoading ? 'Redirecting...' : 'Authorize $750 Intake Fee'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Liquidity Verification Prompt */}
        <Card className="glass-effect border-amber-200 dark:border-amber-800 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-amber-600 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-slate-900 dark:text-slate-100">Verify Your Closing Liquidity</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Verify your available funds for closing costs and project start to speed up the process.
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={() => navigate('/borrower/verification')}
                className="border-amber-400 text-amber-700 hover:bg-amber-100 dark:border-amber-600 dark:text-amber-400 dark:hover:bg-amber-900/30"
              >
                <Shield className="w-4 h-4 mr-2" />
                Verify
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Document Upload */}
        <DocumentUpload dealId={dealId} onUploadSuccess={() => {}} />

        {/* Document List */}
        <DocumentList dealId={dealId} userRole="borrower" />
      </div>
    </Layout>
  );
};

export default BorrowerDealDetail;
